// Layout constants

import { borders } from './borders';

// =============================================================================
// BREAKPOINTS
// =============================================================================
export const breakpoints = {
  // `useIsMobile()`'s default. Deliberately the "is this genuinely a phone"
  // signal — used for decisions that only make sense at true mobile widths:
  // AppShell's compact header/hamburger nav, and hiding complex
  // creation/management surfaces entirely (see Subheader's
  // MOBILE_HIDDEN_ACTIONS and the editor route guard). NOT used for "will
  // this overflow" concerns — those are a different, wider question (see
  // `desktopWidth` below) and every such consumer must pass it explicitly
  // rather than relying on this default, precisely because the two concepts
  // sit at different widths and conflating them previously caused a
  // 768–899px band where nothing fit (AppShell's content area floors at
  // `desktopWidth` once "desktop" layout is assumed — that floor, and
  // anything else answering "is there enough room," must stay keyed to
  // `desktopWidth`, not this narrower mobile threshold).
  mobileWidth: 767,
  mobileHeight: 640,
  desktopWidth: 900,
  desktopMinWidth: 1024,
  desktopMinWidthAnalytics: 1150,
  // Below this viewport width the course and checklist detail left column
  // narrows to `layout.detailPanelNarrowWidth` (#1766, Q-592).
  detailPanelNarrowBelow: 1150,
} as const;

// =============================================================================
// LAYOUT DIMENSIONS
// =============================================================================
export const layout = {
  // Header
  headerHeight: 70,

  // Filter sidebar (left navigation in list views)
  filterSidebarWidth: 240,

  // Admin tool sidebar (shared between Subheader leftWidth and sidebar pane)
  adminSidebarWidth: 220,
  headerShadow: '0 1px 2px -1px rgba(0, 0, 0, 0.2), 0 2px 4px 0 rgba(0, 0, 0, 0.14), 0 1px 10px 0 rgba(0, 0, 0, 0.12)',

  // Panel (left nav)
  panelWidth: 340,
  // Course and checklist detail left column below `breakpoints.detailPanelNarrowBelow` (#1766, Q-592)
  detailPanelNarrowWidth: 280,
  panelPadding: 25,

  // Search/Filter bar
  searchBarHeight: 62,

  // Analytics
  chartHeight: 220,
  analyticsTablePosition: 280,
  selectedComponentHeaderHeight: 30,
  selectedHeaderHeight: 60,
  analyticsContactImageSize: 100,
  objectTitleHeight: 35,
} as const;

// =============================================================================
// BUTTON SIZES (from buttons.less)
// =============================================================================
export const buttonSizes = {
  large: {
    height: 50,
    fontSize: 16,
    iconSize: 20,
  },
  standard: {
    height: 35,
    fontSize: 13,
    iconSize: 18,
  },
  small: {
    height: 30,
    fontSize: 12,
    iconSize: 18,
    width: 150,
  },
  xSmall: {
    height: 25,
    fontSize: 10,
    iconSize: 18,
    width: 150,
  },
} as const;

// =============================================================================
// DIALOG DIMENSIONS (from dialog.less)
// =============================================================================
export const dialog = {
  headerHeight: 45,
  footerHeight: 50,
  bodyPadding: '15px 20px',
  headerFontSize: 16,
  headerFontWeight: 700,
  bodyFontSize: 15,
} as const;

// =============================================================================
// TABS (from tabs.less)
// =============================================================================
export const tabs = {
  height: 29,
  tabWidth: 115,
  closeButtonWidth: 27,
  fontSize: 11,
  borderRadius: borders.radius.image,
} as const;

// =============================================================================
// CARD DIMENSIONS (from cards.less)
// =============================================================================
export const cards = {
  // Ving/Packet card (.app-ving)
  ving: {
    width: 315,
    height: 210,
    headerHeight: 30,
    bodyHeight: 120,
    titleHeight: 42,
    margin: 10,
    boxShadow: '0 4px 5px -2px rgba(0, 0, 0, 0.05), 0 2px 7px 1px rgba(0, 0, 0, 0.05), 0 2px 5px 1px rgba(0, 0, 0, 0.05)',
  },
  // Contact card (.app-contact)
  contact: {
    width: 182,
    height: 170,
    avatarSize: 72,
    fieldHeight: 16,
    headerHeight: 30,
    bodyHeight: 140,
    margin: 10,
    boxShadow: '0 4px 5px -2px rgba(0, 0, 0, 0.05), 0 2px 7px 1px rgba(0, 0, 0, 0.05), 0 2px 5px 1px rgba(0, 0, 0, 0.05)',
  },
  // Organization card (network context)
  organization: {
    width: 200,
    height: 180,
    headerHeight: 30,
    bodyHeight: 150,
    margin: 10,
    boxShadow: '0 4px 5px -2px rgba(0, 0, 0, 0.05), 0 2px 7px 1px rgba(0, 0, 0, 0.05), 0 2px 5px 1px rgba(0, 0, 0, 0.05)',
  },
} as const;

// =============================================================================
// INPUT DIMENSIONS (from controls.less)
// =============================================================================
export const inputs = {
  textInputHeight: 28,
  fieldTextHeight: 27,
  checkboxSize: 23,
  placeholderFontSize: 11,
} as const;

// =============================================================================
// TAG/CHIP DIMENSIONS (from tag.less)
// =============================================================================
export const tags = {
  borderRadius: borders.radius.pill,
  lineHeight: 21,
  fontSize: 8,
  fontWeight: 800,
  // Alt tag (labels)
  alt: {
    lineHeight: 16,
    fontSize: 10,
    fontWeight: 400,
    padding: '0 8px',
    maxTextWidth: 90,
  },
} as const;

// =============================================================================
// FILTER CHIPS (compact sidebar filter chips)
// =============================================================================
export const chips = {
  height: 24,
  fontSize: '0.75rem',
  fontWeight: 500,
  deleteIconSize: 16,
  borderRadius: borders.radius.xs,
} as const;

// =============================================================================
// FILTER SIDEBAR SECTIONS
// =============================================================================
export const filterSections = {
  /** Minimum height for an expanded section (header + a few rows of chips) */
  expandedMinHeight: 200,
  /** Category row minimum height */
  categoryRowHeight: 40,
  /** Tooltip show delay (ms) */
  tooltipDelay: 400,
} as const;

// =============================================================================
// OWNER BADGE (shared between ving and contact cards)
// =============================================================================
export const ownerBadge = {
  size: 30,
  fontSize: 14,
} as const;

// =============================================================================
// HOVER CARD OVERLAY
// =============================================================================
export const hoverOverlay = {
  buttonWidth: 90,
  buttonMargin: 10,
  buttonBoxShadow: '0 5px 7px -3px rgba(0, 0, 0, 0.08), 0 12px 18px 1px rgba(0, 0, 0, 0.05), 0 4px 20px 4px rgba(0, 0, 0, 0.06)',
  overlayBackground: 'rgba(0, 0, 0, 0.288)',
} as const;

// =============================================================================
// TABLE DIMENSIONS
// =============================================================================
export const tables = {
  /** Analytics tables (assignments, submissions, responses) */
  analytics: {
    rowHeight: 49,
    /**
     * Header row height. Tuned to match the filter toolbar's status row (the
     * "2nd row" — `chipBarRow`) so the two horizontal bands line up: ~26px of
     * action-button content + 16px (sm) vertical padding + 2px borders.
     * `chipBarRow` pins its `min-height` to this same value to keep them locked.
     */
    headerHeight: 44,
  },
} as const;

// =============================================================================
// CHART DIMENSIONS
// =============================================================================
export const charts = {
  /** Compact doughnut chart (detail view analytics panes). `size` is now the
   *  MAX — the donut scales down from here toward `donut.minSize` on smaller
   *  viewports to give the table back vertical room. */
  compact: {
    size: 240,
  },
  /** Full dashboard doughnut chart (Paper card wrapper) */
  dashboard: {
    maxSize: 280,
    minHeight: 280,
  },
  /**
   * Shared floor for every doughnut — the diameter it holds at ~1150px on the
   * dashboard. Below this the fixed-size center text collides with the ring, so
   * both surfaces bottom out here: the dashboard donut is floored at it (the
   * legend compresses instead of the donut shrinking further, then the cards
   * stack under 1024px), and the detail donut scales down only to it.
   */
  donut: {
    minSize: 156,
  },
  /** Legend item dimensions (shared by all doughnut variants) */
  legend: {
    dotSize: 12,
    minWidth: 140,
    /** Compressed floor used on the dashboard so the donut can stay at
     *  `donut.minSize` in the 1024–1150px band instead of shrinking. */
    compactMinWidth: 92,
  },
} as const;

// =============================================================================
// ACTION CARD DIMENSIONS
// =============================================================================
export const actionCard = {
  /** Accent border width (severity indicator) */
  accentWidth: 3,
  /** Minimum card width for multi-column wrap */
  minWidth: 240,
  /** Gap between cards in grid */
  gap: 4,
} as const;

// =============================================================================
// MISC SHARED VALUES
// =============================================================================
export const misc = {
  // Scroll loading indicator
  scrollLoadingHeight: 35,
  // Message text styling
  messageTextColor: '#aaa',
  messageTextFontSize: 11,
} as const;
